import {
  isTrolleyClientQueuedSupersededError,
  isTrolleyClientAddTrolleyItemAuthenticationError,
  isTrolleyClientUpdateTrolleyItemAuthenticationError,
  isTrolleyClientRemoveTrolleyItemAuthenticationError,
  isTrolleyClientAddTrolleyItemConflictError,
  isTrolleyClientUpdateTrolleyItemConflictError,
  isTrolleyClientRemoveTrolleyItemConflictError,
  isTrolleyClientQueuedCancelledError,
} from '@johnlewispartnership/wtr-website-trolley-sdk';
import type { TrolleyItemNormalisedConflict } from '@johnlewispartnership/wtr-website-trolley-sdk';

import { logger } from 'logger';
import { unexpectedErrorModal, createTrolleyConflictModal } from 'constants/modals/modals';
import type { GetErrorModal } from 'types/react-query';

const isTrolleyMutationAuthenticationError = (error: unknown) =>
  isTrolleyClientAddTrolleyItemAuthenticationError(error) ||
  isTrolleyClientUpdateTrolleyItemAuthenticationError(error) ||
  isTrolleyClientRemoveTrolleyItemAuthenticationError(error);

const isTrolleyMutationConflictError = (
  error: unknown,
): error is { conflicts: TrolleyItemNormalisedConflict[] } =>
  isTrolleyClientAddTrolleyItemConflictError(error) ||
  isTrolleyClientUpdateTrolleyItemConflictError(error) ||
  isTrolleyClientRemoveTrolleyItemConflictError(error);

const getTopConflict = (conflicts: TrolleyItemNormalisedConflict[]) => {
  const [topConflict] = [...conflicts].sort((a, b) => a.priority - b.priority);

  return topConflict;
};

const getConflictModal = (error: unknown) => {
  if (!isTrolleyMutationConflictError(error)) return undefined;

  const topConflict = getTopConflict(error.conflicts);

  if (!topConflict) return undefined;

  const { reason, title, dismissal } = topConflict.messages;

  return createTrolleyConflictModal(reason, {
    titleText: title,
    dismissalText: dismissal,
  });
};

const logTrolleyMutationError = (error: unknown) => {
  const errorDetails = error instanceof Error ? error : {};

  logger.error({
    payload: {
      message: `Failed to mutate trolley item`,
      service: 'trolley',
      errorDetails,
    },
  });
};

export const getTrolleyMutationErrorModal: GetErrorModal = ({ error }) => {
  if (
    isTrolleyClientQueuedSupersededError(error) ||
    isTrolleyMutationAuthenticationError(error) ||
    isTrolleyClientQueuedCancelledError(error)
  )
    return undefined;

  logTrolleyMutationError(error);

  return getConflictModal(error) ?? unexpectedErrorModal;
};
